import { useState, useEffect } from 'react';

interface GlazingType {
  id: string;
  name: string;
  description: string;
  base_price_per_sqm: number;
  icon: string;
}

interface GlazingOption {
  id: string;
  option_type: string;
  name: string;
  description: string;
  price_type: string;
  price_value: number;
}

export default function GlazingCalculator() {
  const [glazingTypes, setGlazingTypes] = useState<GlazingType[]>([]);
  const [options, setOptions] = useState<GlazingOption[]>([]);
  
  const [selectedGlazing, setSelectedGlazing] = useState<string>('');
  const [width, setWidth] = useState<string>('');
  const [height, setHeight] = useState<string>('');
  const [floor, setFloor] = useState<string>('0');
  const [hasElevator, setHasElevator] = useState<boolean>(false);
  const [needsLift, setNeedsLift] = useState<boolean>(false);
  
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [priceBreakdown, setPriceBreakdown] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: ''
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);

  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedGlazing && width && height) {
      calculatePrice();
    }
  }, [selectedGlazing, width, height, floor, hasElevator, needsLift]);

  const loadData = async () => {
    try {
      // Utiliser AbortController pour timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${SUPABASE_URL}/functions/v1/get-glazing-calculator-data`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      const data = await response.json();
      
      setGlazingTypes(data.glazingTypes || []);
      setOptions(data.options || []);
    } catch (error) {
      console.error('Erreur de chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = () => {
    const widthNum = parseFloat(width) || 0;
    const heightNum = parseFloat(height) || 0;
    const floorNum = parseInt(floor) || 0;
    
    if (widthNum <= 0 || heightNum <= 0) {
      setTotalPrice(0);
      setPriceBreakdown(null);
      return;
    }

    const surfaceArea = (widthNum * heightNum) / 10000;
    
    const glazing = glazingTypes.find(g => g.id === selectedGlazing);
    if (!glazing) return;

    const installOption = options.find(o => o.option_type === 'installation');
    const installationPrice = installOption ? installOption.price_value : 0;

    // Ajout systématique de 90€ HTVA pour la pose
    const posePrice = 90;

    let breakdown: any = {
      surface: surfaceArea,
      glazingPrice: (surfaceArea * glazing.base_price_per_sqm) + installationPrice + posePrice,
      posePrice: posePrice,
      floorPrice: 0,
      liftPrice: 0
    };

    if (surfaceArea >= 1 && floorNum > 0 && !hasElevator) {
      const floorOption = options.find(o => 
        o.option_type === 'floor' && 
        o.name.includes('sans ascenseur')
      );
      if (floorOption) {
        breakdown.floorPrice = floorOption.price_value * floorNum;
      }
    }

    if (surfaceArea >= 1.5 && (needsLift || surfaceArea > 3)) {
      const liftOption = options.find(o => o.option_type === 'lift');
      if (liftOption) {
        breakdown.liftPrice = liftOption.price_value;
      }
    }

    const total = breakdown.glazingPrice + breakdown.floorPrice + breakdown.liftPrice;
    
    setTotalPrice(total);
    setPriceBreakdown(breakdown);
  };

  const selectedGlazingData = glazingTypes.find(g => g.id === selectedGlazing);
  const surfaceArea = (parseFloat(width) || 0) * (parseFloat(height) || 0) / 10000;

  const handleGlazingChange = (glazingId: string) => {
    setSelectedGlazing(glazingId);
    setWidth('');
    setHeight('');
    setFloor('0');
    setHasElevator(false);
    setNeedsLift(false);
    setTotalPrice(0);
    setPriceBreakdown(null);
    
    setTimeout(() => {
      const dimensionsElement = document.getElementById('dimensions-section');
      if (dimensionsElement) {
        const elementRect = dimensionsElement.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
        window.scrollTo({ top: middle, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleDimensionsComplete = () => {
    if (width && height && surfaceArea >= 1) {
      setTimeout(() => {
        const optionsElement = document.getElementById('options-section');
        if (optionsElement) {
          const elementRect = optionsElement.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
          window.scrollTo({ top: middle, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleFloorChange = (newFloor: string) => {
    setFloor(newFloor);
    
    setTimeout(() => {
      const priceElement = document.getElementById('estimated-price-glazing');
      if (priceElement) {
        const elementRect = priceElement.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
        window.scrollTo({ top: middle, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleSubmitContact = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('address', formData.address);
      formBody.append('message', formData.message);
      
      if (selectedGlazingData) {
        formBody.append('glazing_type', selectedGlazingData.name);
      }
      if (width && height) {
        formBody.append('dimensions', `${width}cm x ${height}cm (${surfaceArea.toFixed(2)}m²)`);
      }
      if (totalPrice > 0) {
        formBody.append('estimated_price', `${totalPrice.toFixed(2)}€ HTVA`);
      }

      const response = await fetch('FORM_SUBMIT_URL_PLACEHOLDER', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormSuccess(true);
        setFormData({ name: '', email: '', phone: '', address: '', message: '' });
        setTimeout(() => {
          setShowContactForm(false);
          setFormSuccess(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
    } finally {
      setFormSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-teal-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
            <p className="mt-4 text-gray-600">Chargement du calculateur...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="calculateur-vitrage" className="py-20 bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-teal-300 rounded-full opacity-20 blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-cyan-300 rounded-full opacity-20 blur-3xl translate-x-1/2 translate-y-1/2"></div>
      <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-blue-200 rounded-full opacity-10 blur-3xl -translate-x-1/2 -translate-y-1/2"></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-6 py-3 rounded-full text-sm font-semibold mb-6 shadow-xl">
            <i className="ri-calculator-line text-xl"></i>
            <span>Calculateur Vitrage Professionnel</span>
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Calculateur de Prix Double & Triple Vitrage
          </h2>
          <p className="text-2xl text-gray-700 font-medium max-w-3xl mx-auto">
            Estimation précise en temps réel selon vos dimensions et contraintes
          </p>
          <div className="flex items-center justify-center gap-8 mt-8 flex-wrap">
            <div className="flex items-center gap-2 text-gray-700">
              <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg">
                <i className="ri-ruler-line text-2xl text-teal-600"></i>
              </div>
              <span className="font-semibold">Calcul au cm près</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg">
                <i className="ri-price-tag-3-line text-2xl text-teal-600"></i>
              </div>
              <span className="font-semibold">Prix tout compris</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
              </div>
              <span className="font-semibold">Garantie 2 ans</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8 border-2 border-teal-100">
            <div className="mb-8">
              <label className="block text-lg font-bold text-gray-900 mb-4">
                <span className="inline-flex items-center justify-center w-8 h-8 bg-teal-500 text-white rounded-full text-sm mr-3">1</span>
                Type de Vitrage
              </label>
              <div className="space-y-3">
                {glazingTypes.map((glazing) => (
                  <button
                    key={glazing.id}
                    onClick={() => handleGlazingChange(glazing.id)}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left cursor-pointer ${
                      selectedGlazing === glazing.id
                        ? 'border-teal-500 bg-teal-50 shadow-lg'
                        : 'border-gray-200 bg-white hover:border-teal-300'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                          <i className={`${glazing.icon} text-2xl text-teal-600`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900">{glazing.name}</div>
                          <div className="text-sm text-gray-600">{glazing.description}</div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-lg font-bold text-teal-600">{glazing.base_price_per_sqm}€</div>
                        <div className="text-xs text-gray-500">/m²</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {selectedGlazing && (
              <div id="dimensions-section" className="mb-8">
                <label className="block text-lg font-bold text-gray-900 mb-4">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-teal-500 text-white rounded-full text-sm mr-3">2</span>
                  Dimensions du Vitrage
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Largeur (cm)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={width}
                        onChange={(e) => setWidth(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                        placeholder="120"
                        min="0"
                        step="1"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <i className="ri-ruler-line"></i>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hauteur (cm)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={height}
                        onChange={(e) => setHeight(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                        placeholder="150"
                        min="0"
                        step="1"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <i className="ri-ruler-line"></i>
                      </div>
                    </div>
                  </div>
                </div>
                {surfaceArea > 0 && (
                  <div className="mt-3 p-3 bg-teal-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Surface totale :</span>
                      <span className="text-lg font-bold text-teal-600">{surfaceArea.toFixed(2)} m²</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {width && height && (
              <div id="options-section" className="mb-8">
                <label className="block text-lg font-bold text-gray-900 mb-4">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-teal-500 text-white rounded-full text-sm mr-3">3</span>
                  Options & Contraintes
                </label>
                
                <div className="space-y-4">
                  {surfaceArea >= 1 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Étage d'installation
                      </label>
                      <select
                        value={floor}
                        onChange={(e) => handleFloorChange(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base cursor-pointer"
                      >
                        <option value="0">Rez-de-chaussée</option>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(f => (
                          <option key={f} value={f}>{f}{f === 1 ? 'er' : 'ème'} étage</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {surfaceArea >= 1 && parseInt(floor) > 0 && (
                    <div className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      hasElevator ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300'
                    }`}>
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                          <img 
                            src="https://public.readdy.ai/ai/img_res/da3ee1018f99b221ee2e3fed97153053.jpg"
                            alt="Ascenseur"
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                            decoding="async"
                            width="200"
                            height="200"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            Ascenseur disponible
                            {hasElevator && (
                              <span className="ml-2 text-xs bg-green-500 text-white px-2 py-1 rounded-full">
                                Gratuit
                              </span>
                            )}
                            {!hasElevator && (
                              <span className="ml-2 text-xs bg-amber-500 text-white px-2 py-1 rounded-full">
                                +30€/étage
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-600">
                            {hasElevator ? 'Aucun supplément' : 'Supplément manutention sans ascenseur'}
                          </div>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={hasElevator}
                          onChange={(e) => setHasElevator(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                      </label>
                    </div>
                  )}

                  {surfaceArea >= 1.5 && (
                    <div className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      surfaceArea > 3 ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                          <img 
                            src="https://readdy.ai/api/search-image?query=professional%20window%20glass%20lifting%20equipment%20crane%20machinery%20for%20large%20glazing%20installation%2C%20specialized%20lift%20truck%20with%20suction%20cups%20for%20heavy%20glass%20panels%2C%20industrial%20window%20installation%20equipment%20Brussels%20Belgium%2C%20realistic%20photography%20showing%20professional%20glass%20handling%20machinery&width=200&height=200&seq=lift-equipment-glass-v1&orientation=squarish"
                            alt="Équipement LIFT"
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                            decoding="async"
                            width="200"
                            height="200"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            Option LIFT
                            {surfaceArea > 3 && (
                              <span className="ml-2 text-xs bg-amber-500 text-white px-2 py-1 rounded-full">
                                Recommandé
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-600">
                            {surfaceArea > 3 
                              ? 'Requis pour vitrages > 3m²' 
                              : 'Équipement spécial grands gabarits'}
                          </div>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={needsLift || surfaceArea > 3}
                          onChange={(e) => setNeedsLift(e.target.checked)}
                          disabled={surfaceArea > 3}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500 peer-disabled:opacity-50"></div>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Colonne droite - Résultat */}
          <div className="lg:sticky lg:top-8 h-fit">
            {totalPrice > 0 && priceBreakdown ? (
              <div id="estimated-price-glazing" className="bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-600 rounded-3xl shadow-2xl p-8 text-white">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
                    <i className="ri-price-tag-3-line text-4xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Estimation Totale</h3>
                  <p className="text-teal-100 text-sm">Prix tout compris</p>
                </div>

                {/* Prix principal */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-6">
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-2">
                      {totalPrice.toFixed(2)}€
                    </div>
                    <div className="text-teal-100 text-sm">HTVA</div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/20 flex justify-between text-sm">
                    <span>TVA 6%</span>
                    <span className="font-semibold">{(totalPrice * 0.06).toFixed(2)}€</span>
                  </div>
                  <div className="mt-2 flex justify-between text-lg font-bold">
                    <span>Total TTC</span>
                    <span>{(totalPrice * 1.06).toFixed(2)}€</span>
                  </div>
                </div>

                {/* Détail des prix */}
                <div className="space-y-3 mb-6">
                  <div className="text-sm font-semibold mb-2 text-teal-100">Détail du calcul :</div>
                  
                  <div className="flex justify-between items-center p-3 bg-white/10 rounded-lg">
                    <div className="flex items-center gap-2">
                      <i className="ri-window-2-line"></i>
                      <span className="text-sm">{selectedGlazingData?.name}</span>
                    </div>
                    <span className="font-semibold">{((priceBreakdown.surface * (selectedGlazingData?.base_price_per_sqm || 0))).toFixed(2)}€</span>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-white/10 rounded-lg">
                    <div className="flex items-center gap-2">
                      <i className="ri-tools-line"></i>
                      <span className="text-sm">Pose du vitrage</span>
                    </div>
                    <span className="font-semibold">{priceBreakdown.posePrice.toFixed(2)}€</span>
                  </div>

                  {priceBreakdown.floorPrice > 0 && (
                    <div className="flex justify-between items-center p-3 bg-white/10 rounded-lg">
                      <div className="flex items-center gap-2">
                        <i className="ri-building-line"></i>
                        <span className="text-sm">
                          Étage {floor} (sans ascenseur)
                        </span>
                      </div>
                      <span className="font-semibold">+{priceBreakdown.floorPrice.toFixed(2)}€</span>
                    </div>
                  )}

                  {surfaceArea >= 1 && parseInt(floor) > 0 && hasElevator && (
                    <div className="flex justify-between items-center p-3 bg-green-500/20 rounded-lg border border-green-400/30">
                      <div className="flex items-center gap-2">
                        <i className="ri-building-line"></i>
                        <span className="text-sm">
                          Étage {floor} (avec ascenseur)
                        </span>
                      </div>
                      <span className="font-semibold text-green-300">Gratuit</span>
                    </div>
                  )}

                  {priceBreakdown.liftPrice > 0 && (
                    <div className="flex justify-between items-center p-3 bg-white/10 rounded-lg">
                      <div className="flex items-center gap-2">
                        <i className="ri-truck-line"></i>
                        <span className="text-sm">Option LIFT</span>
                      </div>
                      <span className="font-semibold">+{priceBreakdown.liftPrice.toFixed(2)}€</span>
                    </div>
                  )}

                  {/* Ligne gratuite - Réglage & graissage */}
                  <div className="flex justify-between items-center p-3 bg-green-500/20 rounded-lg border border-green-400/30">
                    <div className="flex items-center gap-2">
                      <i className="ri-tools-line"></i>
                      <span className="text-sm">Réglage & graissage du châssis</span>
                    </div>
                    <span className="font-semibold text-green-300">Gratuit</span>
                  </div>
                </div>

                {/* Caractéristiques */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6">
                  <div className="text-sm font-semibold mb-3">Votre vitrage :</div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-teal-100 text-xs mb-1">Dimensions</div>
                      <div className="font-semibold">{width} × {height} cm</div>
                    </div>
                    <div>
                      <div className="text-teal-100 text-xs mb-1">Surface</div>
                      <div className="font-semibold">{priceBreakdown.surface.toFixed(2)} m²</div>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="space-y-3">
                  <a
                    href="https://wa.me/32800204100"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-white text-teal-600 px-6 py-4 rounded-xl font-bold text-center hover:bg-teal-50 transition-all shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-fill text-xl mr-2"></i>
                    Confirmer par WhatsApp
                  </a>
                  <button
                    onClick={() => setShowContactForm(true)}
                    className="block w-full bg-white/20 backdrop-blur-sm text-white px-6 py-4 rounded-xl font-bold text-center hover:bg-white/30 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-mail-line text-xl mr-2"></i>
                    Demander un devis détaillé
                  </button>
                </div>

                <p className="text-center text-xs text-teal-100 mt-4">
                  <i className="ri-information-line mr-1"></i>
                  Prix indicatif - Confirmation après diagnostic sur place
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-3xl shadow-2xl p-8 text-center border-2 border-teal-100">
                <div className="w-24 h-24 flex items-center justify-center bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full mx-auto mb-6">
                  <i className="ri-calculator-line text-5xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Configurez votre vitrage
                </h3>
                <p className="text-gray-600">
                  Sélectionnez le type de vitrage et renseignez les dimensions pour obtenir votre estimation
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Formulaire de Contact */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-6 rounded-t-3xl flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-1">Demande de Devis Détaillé</h3>
                <p className="text-teal-100 text-sm">Nous vous répondons sous 24h</p>
              </div>
              <button
                onClick={() => setShowContactForm(false)}
                className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-all cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmitContact} className="p-8" data-readdy-form id="contact-vitrage-form">
              {formSuccess && (
                <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-xl flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-500 rounded-full flex-shrink-0">
                    <i className="ri-check-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-green-900">Message envoyé avec succès !</p>
                    <p className="text-sm text-green-700">Nous vous contacterons rapidement.</p>
                  </div>
                </div>
              )}

              {totalPrice > 0 && (
                <div className="mb-6 p-4 bg-teal-50 border-2 border-teal-200 rounded-xl">
                  <h4 className="font-bold text-gray-900 mb-2">Votre configuration :</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Type :</span>
                      <span className="ml-2 font-semibold text-gray-900">{selectedGlazingData?.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Dimensions :</span>
                      <span className="ml-2 font-semibold text-gray-900">{width} × {height} cm</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Surface :</span>
                      <span className="ml-2 font-semibold text-gray-900">{surfaceArea.toFixed(2)} m²</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Prix estimé :</span>
                      <span className="ml-2 font-semibold text-teal-600">{totalPrice.toFixed(2)}€ HTVA</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                    placeholder="Jean Dupont"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                      placeholder="jean@exemple.be"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                      placeholder="0800.20.410"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Adresse d'installation *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                    placeholder="Rue de la Loi 1, 1000 Bruxelles"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message complémentaire
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base resize-none"
                    placeholder="Précisez vos besoins, contraintes d'accès, délais souhaités..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 caractères
                  </p>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowContactForm(false)}
                  className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="flex-1 px-6 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-bold hover:from-teal-600 hover:to-cyan-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {formSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill mr-2"></i>
                      Envoyer la demande
                    </>
                  )}
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">
                <i className="ri-lock-line mr-1"></i>
                Vos données sont sécurisées et ne seront jamais partagées
              </p>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
